import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { getCurrentCountryContext } from '../../../../helpers/countriesHelpers';
import { formatProp } from '../../../../helpers/formatProp';
import { t } from '../../../../i18n';

const initState = {
  accreditation: null,
  editForm: null,
  loading: false,
  formInfo: {
    countries: [],
  },
};

const getInitialAccreditationData = () => ({
  name: null,
  summary: null,
  description: null,
  country: getCurrentCountryContext().id,
  sector_ids: [],
  state_ids: [],
  positions: [],
  worker_photo_required: false,
  expiration_date_required: false,
  visibility: null,
  bill: 0,
  pay: 0,
  level: null,
  icon: null,
  example_document_image: null,
});

function validate(attr, value) {
  let error = '';

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.accreditations.name_label') });
    }
  }

  if (attr === 'summary') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.accreditations.summary_label') });
    }
  }

  if (attr === 'description') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.admin.accreditations.description_label'),
      });
    }
  }

  if (attr === 'visibility') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_an_option');
    }
  }

  if (attr === 'level') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_an_option');
    }
  }

  if (attr === 'sector_ids') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.accreditations.sectors_label') });
    }
  }

  if (attr === 'state_ids') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.accreditations.states_label') });
    }
  }

  if (attr === 'position_ids') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.admin.accreditations.positions_label'),
      });
    }
  }

  return error;
}

function onClearStore() {
  return { ...initState };
}

function onSaveSuccess(state) {
  return {
    ...state,
    accreditation: null,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [prop, value] = entry;

      return prop === 'positions'
        ? value
        : {
            ...value,
            error: getError(entry[0], data),
            touched: true,
          };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, silent } = payload;
  const { editForm } = state;
  const edittedForm = {};
  edittedForm[attr] = silent
    ? value
    : {
        value,
        error: validate(attr, value),
        name: attr,
        touched: true,
      };

  return {
    ...state,
    editForm: {
      ...editForm,
      ...edittedForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [prop, value] = entry;

      return prop === 'positions'
        ? value
        : {
            ...value,
            touched: true,
          };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onCreate(state) {
  const initialAccreditationData = getInitialAccreditationData();
  const propsArray = Object.keys(initialAccreditationData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop =>
      prop === 'positions'
        ? initialAccreditationData[prop]
        : {
            name: prop,
            value: initialAccreditationData[prop],
            error: validate(prop, initialAccreditationData[prop]),
            touched: false,
          },
  );

  return {
    ...state,
    accreditation: initialAccreditationData,
    isEdit: true,
    editForm: {
      ...editForm,
    },
  };
}

function onUpdateDefault(state) {
  const { editForm } = state;
  const { state_ids, sector_ids, worker_photo_required, expiration_date_required, bill, pay } = editForm;

  return {
    ...state,
    editForm: {
      ...editForm,
      worker_photo_required: { ...worker_photo_required, touched: true },
      expiration_date_required: { ...expiration_date_required, touched: true },
      bill: { ...bill, touched: true },
      pay: { ...pay, touched: true },
      state_ids: { ...state_ids, value: [] },
      sector_ids: { ...sector_ids, value: [] },
      positions: [],
    },
  };
}

function onLoadFormData(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: { ...data },
  };
}

function onLoadData(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    loading: false,
    accreditation: data,
  };
}

function onEdit(state, { payload }) {
  const { data } = payload;
  const propsArray = Object.keys(data);

  const newPropsArray = propsArray.filter(prop => {
    return (
      prop !== 'icon_url' &&
      prop !== 'example_document_image_url' &&
      prop !== 'positions' &&
      prop !== 'states' &&
      prop !== 'sectors'
    );
  });

  const editForm = keyBy(
    newPropsArray,
    prop => prop,
    prop =>
      prop === 'positions'
        ? data[prop]
        : {
            name: prop,
            value: data[prop] && {}.propertyIsEnumerable.call(data[prop], 'id') ? data[prop].id : data[prop],
            error: validate(prop, data[prop]),
            touched: false,
          },
  );

  const renamedProps = {
    sector_ids: { value: data.sectors.map(sector => sector.id), name: 'sectors', error: '', touched: false },
    state_ids: { value: data.states.map(item => item.id), name: 'states', error: '', touched: false },
    positions: data.positions,
    icon: { value: data.icon_url, name: 'icon_url', error: '', touched: false },
    example_document_image: {
      value: data.example_document_image_url,
      name: 'example_document_image_url',
      error: '',
      touched: false,
    },
  };

  return {
    ...state,
    isEdit: true,
    accreditation: data,
    editForm: {
      ...editForm,
      ...renamedProps,
    },
  };
}

function getValue(attrs, value) {
  if (typeof value === 'object' && value !== null) {
    return keyBy(
      Object.keys(value),
      prop => prop,
      prop => formatProp(value, prop, validate),
    );
  }

  return {
    name: attrs.join('.'),
    touched: true,
    error: validate(attrs.join('.'), value),
    value,
  };
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value, silent } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];
    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = silent ? value : getValue(attrs, value, state);
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = silent ? value : getValue(attrs, value, state);
  } else {
    editForm[attrs[0]] = silent ? value : getValue(attrs, value, state);
  }

  return { ...state, editForm };
}

export { validate };
export default createReducer(initState, {
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.TOUCH]: onTouch,
  [actions.EDIT]: onEdit,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormData,
  [actions.CREATE]: onCreate,
  [actions.UPDATE_DEFAULT]: onUpdateDefault,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.LOAD_DATA]: onLoadData,
  [actions.LOAD_DATA_SUCCESS]: onLoadDataSuccess,
});
